// src/pages/MyAccountPage.js
import React, {useEffect, useState} from 'react';
import {
    UserCircleIcon,
    KeyIcon,
    BellAlertIcon,
    ArrowRightStartOnRectangleIcon,
    TrashIcon,
    PhotoIcon,
} from '@heroicons/react/24/outline';
import DashboardLayout from "../../components/test/DashboardLayout";
import Toggle from "../../components/common/Toggle";
import {useNavigate, useParams} from "react-router-dom";
import {onValue, ref} from "firebase/database";
import {auth, db} from "../../firebase";
import {toast} from "react-toastify";
import {deleteUser, EmailAuthProvider, reauthenticateWithCredential, updatePassword} from "firebase/auth";

const MyAccountPage = () => {
    const {userId, businessId} = useParams();
    const navigate = useNavigate();

    // Mock User Data (replace with actual fetched user data)
    const [userProfile, setUserProfile] = useState({});

    // State for Password & Security
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
    const [lastLogin, setLastLogin] = useState('June 4, 2025, 08:30 AM (Sandton, South Africa)'); // Mock data

    // State for Notification Preferences
    const [notificationPreferences, setNotificationPreferences] = useState({
        marketingUpdates: true,
        productAnnouncements: true,
        securityAlerts: true,
        systemNotifications: true,
    });

    useEffect(() => {
        onValue(ref(db, `localRewards/account/${userId}`), snapshot => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                setUserProfile({ ...data })
            }
        });
    }, [userId])

    // --- Handlers for Profile Information ---
    const handleProfileSave = (e) => {
        e.preventDefault();

        db.ref('localRewards').child(`/account/${userId}`).update({
            fullName: userProfile.fullName,
            phone: userProfile.phone,
        }).then(() => {
            toast.success('Profile saved successfully!');
        }).catch((err) => {
            toast.error(err.message);
        })
    };

    const handleProfilePictureUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setUserProfile({ ...userProfile, profilePictureUrl: reader.result });
                alert('Profile picture uploaded (preview only)!');
            };
            reader.readAsDataURL(file);
        }
    };

    // --- Handlers for Password & Security ---
    const handleChangePassword = (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            toast.error('New password and confirmation do not match.');
            return;
        }
        if (newPassword.length < 8) {
            toast.error('New password must be at least 8 characters long.');
            return;
        }

        try {
            const credential = EmailAuthProvider.credential(auth?.currentUser?.email, currentPassword)
            reauthenticateWithCredential(auth?.currentUser, credential).then(r => {
                if (r) {
                    updatePassword(auth?.currentUser, newPassword).then(() => {
                        toast.success('Password updated successfully!');

                        setCurrentPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                    }).catch((err) => {
                        toast.error(err.message);
                    })

                }
            }).catch((err) => {
                toast.error(err.message);
            })
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleTwoFactorToggle = (checked) => {
        setTwoFactorEnabled(checked);
        if (checked) {
            alert('Two-Factor Authentication enabled! (A real app would start the setup process)');
        } else {
            alert('Two-Factor Authentication disabled!');
        }
    };

    // --- Handlers for Notification Preferences ---
    const handleNotificationToggle = (prefName, checked) => {
        setNotificationPreferences(prev => ({ ...prev, [prefName]: checked }));
        // No immediate save, will save with the button
    };

    const handleNotificationSave = (e) => {
        e.preventDefault();
        alert('Notification preferences saved!');
        console.log('Saved Notification Prefs:', notificationPreferences);
        // In a real app, send to API
    };

    // --- Handler for Logout ---
    const handleLogout = () => {
        // if (window.confirm('Are you sure you want to log out?')) {
        //     alert('You have been logged out.');
        //     // In a real app, clear auth tokens and redirect to login page
        //     window.location.href = '/login'; // Example redirect
        // }
        auth.signOut().then(() => {
            toast.success('Logout successfully!');
            navigate("/")
        }).catch((err) => {
            toast.error(err.message);
        })
    };

    // --- Handler for Delete Account ---
    const handleDeleteAccount = () => {
        if (window.confirm('WARNING: Are you absolutely sure you want to delete your personal LocalRewards account? This will remove your access to the business account. This action is irreversible.')) {
            deleteUser(auth?.currentUser).then(() => {
                toast.success('Deleted account successfully!');
            }).catch((err) => {
                toast.error(err.message);
            }).finally(() => {

            })
        }

    };


    return (
        <DashboardLayout userId={userId} businessId={businessId}>
            <h1 className="text-3xl font-bold text-gray-800 mb-6">My Account</h1>

            <div className="bg-white rounded-lg shadow-md p-6 space-y-8">

                {/* --- 1. Profile Information --- */}
                <section className="border-b border-gray-200 pb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 flex items-center mb-4">
                        <UserCircleIcon className="h-7 w-7 text-indigo-500 mr-3" />
                        Profile Information
                    </h2>
                    <p className="text-gray-600 mb-6">Update your personal details visible within LocalRewards.</p>

                    <form onSubmit={handleProfileSave} className="space-y-4">
                        {/* Profile Picture */}
                        <div className="flex items-center space-x-6">
                            {userProfile.profilePictureUrl ? (
                                <img src={userProfile.profilePictureUrl} alt="Profile" className="h-24 w-24 rounded-full object-cover border border-gray-200" />
                            ) : (
                                <div className="h-24 w-24 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200">
                                    <PhotoIcon className="h-12 w-12 text-gray-400" />
                                </div>
                            )}
                            <div>
                                <input
                                    type="file"
                                    id="profile-picture-upload"
                                    accept="image/*"
                                    onChange={handleProfilePictureUpload}
                                    className="hidden"
                                />
                                <label
                                    htmlFor="profile-picture-upload"
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                    Change Photo
                                </label>
                                <p className="mt-1 text-xs text-gray-500">JPG, GIF or PNG. Max 5MB.</p>
                            </div>
                        </div>
                        <div className="space-y-4 sm:space-y-0 grid grid-cols-1 sm:grid-cols-2 gap-8">
                            {/* Full Name */}
                            <div>
                                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">Full Name</label>
                                <input
                                    type="text"
                                    id="fullName"
                                    value={userProfile.fullName}
                                    onChange={(e) => setUserProfile({ ...userProfile, fullName: e.target.value })}
                                    className="appearance-none rounded-none mt-1 relative block w-full p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                            </div>

                            {/* Email (Read-only for login) */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={userProfile.email}
                                    readOnly
                                    className="appearance-none rounded-none cursor-not-allowed relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                                <p className="mt-1 text-xs text-gray-500">Your email is used for login and cannot be changed here. Contact support if needed.</p>
                            </div>

                            {/* Phone Number */}
                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number (Optional)</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    value={userProfile.phone}
                                    onChange={(e) => setUserProfile({ ...userProfile, phone: e.target.value })}
                                    className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                            </div>

                            {/* Role (Read-only) */}
                            <div>
                                <label htmlFor="role" className="block text-sm font-medium text-gray-700">Your Role</label>
                                <input
                                    type="text"
                                    id="role"
                                    value={userProfile.role}
                                    readOnly
                                    className="mt-1 bg-gray-50 cursor-not-allowed shadow-sm appearance-none rounded-none relative block w-full p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                                <p className="mt-1 text-xs text-gray-500">Your role is managed by the business administrator in Settings &gt; Users & Roles.</p>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Save Profile
                            </button>
                        </div>
                    </form>
                </section>

                {/* --- 2. Password & Security --- */}
                <section className="border-b border-gray-200 pb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 flex items-center mb-4">
                        <KeyIcon className="h-7 w-7 text-indigo-500 mr-3" />
                        Password & Security
                    </h2>
                    <p className="text-gray-600 mb-6">Manage your login credentials and security settings.</p>

                    <form onSubmit={handleChangePassword} className="space-y-4 mb-6">
                        <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
                        <div>
                            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">Current Password</label>
                            <input
                                type="password"
                                id="currentPassword"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">New Password</label>
                            <input
                                type="password"
                                id="newPassword"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                required
                                minLength="8"
                            />
                        </div>
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">Confirm New Password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="appearance-none rounded-none relative block w-full mt-1 p-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                required
                                minLength="8"
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Update Password
                            </button>
                        </div>
                    </form>

                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-900 mt-6">Other Security Settings</h3>
                        <div className="flex items-center justify-between">
                            <div>
                                <label htmlFor="twoFactorEnabled" className="block text-sm font-medium text-gray-700">Two-Factor Authentication (2FA)</label>
                                <p className="mt-1 text-xs text-gray-500">Add an extra layer of security when logging in.</p>
                            </div>
                            <Toggle
                                id="twoFactorEnabled"
                                checked={twoFactorEnabled}
                                onChange={handleTwoFactorToggle}
                            />
                        </div>

                        <div>
                            <h4 className="text-sm font-medium text-gray-700">Last Login</h4>
                            <p className="mt-1 text-sm text-gray-600">{lastLogin}</p>
                            {/* Could add a "View Login History" button here */}
                        </div>
                    </div>
                </section>

                {/* --- 3. Notification Preferences --- */}
                <section className="border-b border-gray-200 pb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 flex items-center mb-4">
                        <BellAlertIcon className="h-7 w-7 text-indigo-500 mr-3" />
                        Notification Preferences
                    </h2>
                    <p className="text-gray-600 mb-6">Choose what type of communications you'd like to receive from LocalRewards.</p>

                    <form onSubmit={handleNotificationSave} className="space-y-4">
                        <div className="flex items-center justify-between border-b border-dashed py-4">
                            <div>
                                <label htmlFor="marketingUpdates" className="block text-sm font-medium text-gray-700">Marketing Updates</label>
                                <p className="mt-1 text-xs text-gray-500">Occasional emails about LocalRewards features, tips, and promotions.</p>
                            </div>
                            <Toggle
                                id="marketingUpdates"
                                checked={notificationPreferences.marketingUpdates}
                                onChange={(checked) => handleNotificationToggle('marketingUpdates', checked)}
                            />
                        </div>

                        <div className="flex items-center justify-between border-b border-dashed py-4">
                            <div>
                                <label htmlFor="productAnnouncements" className="block text-sm font-medium text-gray-700">Product Announcements</label>
                                <p className="mt-1 text-xs text-gray-500">Notifications about new features, improvements, and changes to LocalRewards.</p>
                            </div>
                            <Toggle
                                id="productAnnouncements"
                                checked={notificationPreferences.productAnnouncements}
                                onChange={(checked) => handleNotificationToggle('productAnnouncements', checked)}
                            />
                        </div>

                        <div className="flex items-center justify-between border-b border-dashed py-4">
                            <div>
                                <label htmlFor="securityAlerts" className="block text-sm font-medium text-gray-700">Security Alerts</label>
                                <p className="mt-1 text-xs text-gray-500">Important notices about your account security (e.g., suspicious login attempts).</p>
                            </div>
                            <Toggle
                                id="securityAlerts"
                                checked={notificationPreferences.securityAlerts}
                                onChange={(checked) => handleNotificationToggle('securityAlerts', checked)}
                            />
                        </div>

                        <div className="flex items-center justify-between py-4">
                            <div>
                                <label htmlFor="systemNotifications" className="block text-sm font-medium text-gray-700">System Notifications</label>
                                <p className="mt-1 text-xs text-gray-500">Essential updates regarding service availability, billing, or critical issues.</p>
                            </div>
                            <Toggle
                                id="systemNotifications"
                                checked={notificationPreferences.systemNotifications}
                                onChange={(checked) => handleNotificationToggle('systemNotifications', checked)}
                            />
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Save Preferences
                            </button>
                        </div>
                    </form>
                </section>

                {/* --- 4. Actions --- */}
                <section className="space-y-4">
                    <h2 className="text-2xl font-semibold text-gray-800 flex items-center mb-4">
                        <ArrowRightStartOnRectangleIcon className="h-7 w-7 text-indigo-500 mr-3" />
                        Account Actions
                    </h2>
                    <p className="text-gray-600 mb-6">Perform critical actions related to your user account.</p>

                    <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
                        <button
                            onClick={handleLogout}
                            className="flex-1 inline-flex items-center justify-center py-2 px-4 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            <ArrowRightStartOnRectangleIcon className="h-5 w-5 mr-2" />
                            Log Out
                        </button>
                        <button
                            onClick={handleDeleteAccount}
                            className="flex-1 inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                            <TrashIcon className="h-5 w-5 mr-2" />
                            Delete My Account
                        </button>
                    </div>
                </section>

            </div>
        </DashboardLayout>
    );
};

export default MyAccountPage;